import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SimpleTable from "@/PageComponents/SimpleTable/SimpleTable";
import * as Enums from "@/utils/enums";
import { IconTrash } from "@tabler/icons";
import permissionService from "@/services/permission/permission-service";
import { Anchor, Button, Flex, Menu } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import NewText from "@/PageComponents/Premium/NewText";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import { useDebouncedState, useDidUpdate } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import featureService from "@/services/feature/feature-service";
import constants from "@/utils/constants";
import helper from "@/utils/helper";
import { calculateLineTotal, calculateInclusive, calculateExclusiveUnitPrice } from '@/utils/line-totals';
import SCModal from "@/PageComponents/Modal/SCModal";
import ImageWithZoom from "@/PageComponents/Attachment/ImageWithZoom";


const addMissingIDs = (items: any[]) => {
    return items.map((x: any, i: any) => x.ID ? x : { ...x, ID: crypto.randomUUID() || 'item' + i })
}


const PurchasesSectionTable: FC<{
    purchaseOrder
    purchaseOrderItems,
    onDataUpdate,
    onItemClicked,
    tableActionStates,
    companyTaxPercentage,
    integration,
    addOptions,
    onAddAction,
    itemId,
    customChildren,
    onColumnMappingLoaded
    userColumnConfig
    descriptionColumnWidth?: string
}> = (props: any) => {

    const { data: hasStockControl } = useQuery(['hasStockControl'], () => featureService.getFeature(constants.features.STOCK_CONTROL));

    const [showImage, setShowImage] = useState<any>();

    const canUpdate = useMemo(() => props.purchaseOrder.PurchaseOrderStatus === Enums.PurchaseOrderStatus.Draft, [props.purchaseOrder.PurchaseOrderStatus])

    const [hasManageCostingPermission] = useState(permissionService.hasPermission(Enums.PermissionName.ManageCosting));

    const [currentData, setCurrentData] = useState(
        addMissingIDs(props.purchaseOrderItems)
    )

    const [currentSections, setCurrentSections] = useState<any[]>([])

    const [savingItem, setSavingItem] = useState(false)

    /** debounce all data updates and only trigger onUpdate callback when data was changed internally */
    const [debounced, setDebounced] = useDebouncedState([currentData, currentSections], 0)
    const changed = useRef(false)
    useEffect(() => {
        setDebounced([currentData, currentSections])
    }, [currentData, currentSections])
    useEffect(() => {
        if (changed.current) {
            props.onDataUpdate(debounced[0], debounced[1])
            changed.current = false
            setSavingItem(true)
        }
    }, [debounced]);

    /** update current data when data changes externally - update only row version if data change was triggered with table data update internally*/
    const cancelNextUpdate = useRef(false)
    useDidUpdate(() => {
        if (props.purchaseOrderItems) {
            if (!cancelNextUpdate.current) {
                const itemsWithIDs = addMissingIDs(props.purchaseOrderItems)

                setCurrentData(itemsWithIDs)
                /*props.quoteItems.length !== currentData.length && // if length changed the data was modified form outside and history needs to be appended
                setHistory((p) => ([...p, [
                    itemsWithIDs,
                    getSectionsFromTableData(itemsWithIDs, 'InventorySectionName', 'InventorySectionID', Enums.Module.JobCard, props.itemId)
                ]]))*/
            } else {
                cancelNextUpdate.current = false
            }
            setSavingItem(false)
        }
    }, [props.purchaseOrderItems])

    const handleDataUpdate = (newData: any/*, newSections, updateHistory = true*/) => {
        setCurrentData(newData)
        changed.current = true
        cancelNextUpdate.current = true
        // updateHistory && setHistory((p) => [...p, [newData, newSections]])
    }

    const handleInputChange = (key: any, item: any, newValue: any) => {
        if (key === 'Description' || key === 'InventoryDescription') {
            handleDataUpdate(
                currentData.map((x: any) => x.ID === item.ID ? ({ ...x, Description: newValue, InventoryDescription: newValue }) : x),
                // currentSections
            )
        } else if (key === 'UnitPriceInclusive') {
            // Extract tax percentage from the item
            const taxPercentage = +item.TaxPercentage || 0;

            // Create a new item with updated values
            const newItem = {
                ...item,
                UnitPriceExclusive: calculateExclusiveUnitPrice(newValue, taxPercentage)
            };

            // Calculate line total based on the new UnitPriceExclusive
            if (newItem.Quantity > 0) {
                newItem.LineTotalExclusive = calculateLineTotal({
                    quantity: newItem.Quantity,
                    unitPrice: newItem.UnitPriceExclusive,
                    discountPercentage: newItem.LineDiscountPercentage,
                });
            } else {
                newItem.LineTotalExclusive = 0;
            }

            handleDataUpdate(
                currentData.map((x: any) => x.ID === item.ID ? ({ ...x, ...newItem }) : x)
            );
        } else if (key === 'Quantity' || key === 'TaxPercentage' || key === 'LineDiscountPercentage' || key === 'UnitPriceExclusive') {
            const newItem = {
                ...item,
                [key]: newValue
            }
            if (newItem.LineDiscountPercentage < 0 || newItem.LineDiscountPercentage > 100) {
                newItem.LineDiscountPercentage = newItem.LineDiscountPercentage < 0 ? 0 : 100
            }
            if (newItem.Quantity > 0) {
                const taxPercentage = newItem.TaxPercentage || 0;

                newItem.LineTotalExclusive = calculateLineTotal({
                    quantity: newItem.Quantity,
                    unitPrice: newItem.UnitPriceExclusive,
                    discountPercentage: newItem.LineDiscountPercentage,
                });
                newItem.LineTotalInclusive = calculateInclusive(newItem.LineTotalExclusive, taxPercentage);
                newItem.UnitPriceInclusive = calculateInclusive(newItem.UnitPriceExclusive, taxPercentage);
            } else {
                newItem.LineTotalExclusive = 0;
                newItem.LineTotalInclusive = 0;
                newItem.UnitPriceInclusive = 0;
            }

            handleDataUpdate(
                currentData.map((x: any) => x.ID === item.ID ? ({ ...x, ...newItem }) : x),
                // currentSections
            )
        } else {
            handleDataUpdate(
                currentData.map((x: any) => x.ID === item.ID ? ({ ...x, [key]: newValue }) : x),
                // currentSections
            )
        }
    }

    const handleItemAction = (actionName: string, item: any, itemIndex: number, group: any) => {
        if (actionName === 'code') {
            props.onItemClicked(item, itemIndex)
        } else if (actionName === 'delete') {
            handleDataUpdate(currentData.filter((x: any) => x.ID !== item.ID)/*, newSections*/)
        }
    }
    /*const handleSectionAction = (name, group) => {
        if (name === 'delete') {
            handleDataUpdate(
                currentData.filter(x => x.InventorySectionID !== group.id).map((x, i) => ({ ...x, LineNumber: i + 1 })),
                // currentSections.filter(x => x.ID !== group.id)
            )
        } else {
            // props.onAddToSectionAction(name, group)
        }
    }*/

    const mapping = useMemo(() =>
            [
                ...((currentData ?? []).filter((x: any) => x.Inventory?.ThumbnailUrl).length > 0 ? [{
                    key: 'ImageUrl',
                    label: '',
                    valueFunction: (item: any) => item.Inventory?.ThumbnailUrl && <img
                        src={item.Inventory?.ThumbnailUrl}
                        style={{ height: 36, width: 36, borderRadius: 36, cursor: "pointer" }}
                        onClick={() => setShowImage(item)}
                    />
                }] : []),
                {
                    key: 'InventoryCode',
                    label: 'Code',
                    valueFunction: (item: any) => item.ProductID ? item.ProductNumber : item.InventoryCode,
                    linkAction: 'code'
                },
                {
                    key: 'Description',
                    label: 'Description',
                    // type: canUpdate ? 'textInput' : undefined,
                    typeFunction: (item: any) => item.InventoryID ? 'textInput' : 'textArea',
                    stylingProps: {
                        miw: props.descriptionColumnWidth ?? '30vw',
                        darkerText: true
                    },
                    inputProps: {
                        width: '100%'
                    }
                },
                ...(props.integration ? [
                    {
                        key: 'ItemType',
                        label: 'Status',
                        type: 'status',
                        colorFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory ? (item.Integrated ? 'Green' : item.IntegrationMessage ? `Yellow` : undefined) : undefined),
                        valueFunction: (item: any) => (
                            item.ItemType === Enums.ItemType.Inventory && (item.Integrated ? 'Synced' : item.IntegrationMessage ? 'Error' : 'Not Synced'
                            ) || null),
                        showFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory,
                    }
                ] : []) as any,
                {
                    key: 'Quantity',
                    label: 'Quantity',
                    typeFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    min: 0,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory)
                },
                ...(hasStockControl &&
                (props.purchaseOrder?.PurchaseOrderStatus === Enums.PurchaseOrderStatus.Approved || props.purchaseOrder?.PurchaseOrderStatus === Enums.PurchaseOrderStatus.Billed) ? [{
                    key: 'QuantityReceived',
                    label: 'Received',
                    alignRight: true,
                    showFunction: (item: any) => helper.isInventoryWarehoused(item.Inventory),
                    valueFunction: (item: any) => item.QuantityReceived ?? 0
                }] : []),
                {
                    key: 'UnitPriceExclusive',
                    label: 'Price Excl',
                    typeFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    // min: 0,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: true
                    }
                },
                {
                    key: 'UnitPriceInclusive',
                    label: 'Price Incl',
                    typeFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true,
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    valueFunction: (item: any) => {
                        // Calculate UnitPriceInclusive from UnitPriceExclusive and TaxPercentage
                        const unitPriceExclusive = item.UnitPriceExclusive || 0;
                        const taxPercentage = item.TaxPercentage || 0;
                        const unitPriceInclusive = unitPriceExclusive * (1 + (taxPercentage / 100));
                        return unitPriceInclusive;
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: false
                    }
                },
                {
                    key: 'LineDiscountPercentage',
                    label: 'Discount %',
                    typeFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    numberInputProps: {
                        min: 0,
                        max: 100
                    },
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'TaxPercentage',
                    label: 'Tax Rate',
                    valueFunction: ((item: any) => item.TaxPercentage + ''),
                    typeFunction: (item: any) => item.ItemType === Enums.ItemType.Inventory ? (canUpdate ? 'selectInput' : undefined) : undefined,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    selectOptions: [
                        {
                            label: 'Standard Rate',
                            value: props.companyTaxPercentage + ''
                        },
                        {
                            label: props.companyTaxPercentage === 0 ? 'Default (No VAT)' : 'No VAT',
                            value: 0 + ''
                        },
                    ].filter((x: any) => x.label !== 'Standard Rate' || props.companyTaxPercentage !== 0),
                    alignRight: true,
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'LineTotalExclusive',
                    label: 'Total Excl',
                    currencyValue: true,
                    alignRight: true,
                    // type: canUpdate ? 'numberInput' : undefined,
                    min: 0,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: true
                    }
                },
                {
                    key: 'LineTotalInclusive',
                    label: 'Total Incl',
                    currencyValue: true,
                    alignRight: true,
                    min: 0,
                    showFunction: (item: any) => (item.ItemType === Enums.ItemType.Inventory),
                    valueFunction: (item: any) => {
                        // Calculate LineTotalInclusive from LineTotalExclusive and TaxPercentage
                        const lineTotalExclusive = item.LineTotalExclusive || 0;
                        const taxPercentage = item.TaxPercentage || 0;
                        const lineTotalInclusive = lineTotalExclusive * (1 + (taxPercentage / 100));
                        const rounded = helper.roundToTwo(lineTotalInclusive);
                        return rounded ? rounded / 100 : null;
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: false
                    }
                }
            ]
        , [currentData, canUpdate, hasManageCostingPermission, props.companyTaxPercentage, props.descriptionColumnWidth, props.integration])

    useEffect(() => {
        props.onColumnMappingLoaded && props.onColumnMappingLoaded(mapping)
    }, [mapping]);

    return <>
        <SectionTable
            userColumnConfig={props.userColumnConfig}
            customChildren={props.customChildren}
            allowSections={false}
            onPredictedDefaultSectionPdfSettingsChanged={console.log}
            onSectionItem={console.log}
            sectionControls={[]}
            module={Enums.Module.PurchaseOrder}
            onDataUpdate={handleDataUpdate}
            data={currentData}
            height={'100%'}
            sectionTitleKey={'InventorySectionName'}
            sectionIdKey={'InventorySectionID'}
            itemId={props.itemId}
            onAction={handleItemAction}
            canEdit={canUpdate && !!props.purchaseOrder.SupplierID}
            stylingProps={{
                compact: true,
                rows: false,
                darkerText: true,
            }}
            onInputChange={handleInputChange}
            mapping={mapping}
            controls={[
                ...(canUpdate ? [{
                    name: 'delete',
                    // type: 'warning',
                    icon: <IconTrash />,
                    label: 'Remove',
                    activeLabel: 'Removing'
                }] : [])
            ]}
            addButton={canUpdate ? {
                label: '',
                // callback: props.onAddItem,
                customComponent: <Flex w={'100%'} gap={'md'}>

                    {!!props.purchaseOrder.SupplierID ?

                        <Menu
                            shadow="md"
                        >
                            <Menu.Target>
                                <Button c={'scBlue'} size={'compact-sm'} variant={'transparent'} type={'button'} >
                                    <IconPlus size={16} />
                                    <Anchor size={'sm'}>
                                        Add Line
                                    </Anchor>
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                {
                                    props.addOptions.map(
                                        (x: any, i: any) => (
                                            <Menu.Item key={x.text + i} onClick={() => props.onAddAction && props.onAddAction(x.link)}>
                                                {x.text} {x.newOption && <NewText h={12} />}
                                            </Menu.Item>
                                        )
                                    )
                                }
                            </Menu.Dropdown>
                        </Menu> : <div style={{ color: "orange", fontStyle: "italic", margin: "0.5rem 0 0.5rem -0.5rem" }}>Select a <span style={{ fontWeight: "bold" }}>supplier above</span> to add lines</div>
                    }
                </Flex>
            } : undefined}
        />

        {showImage &&
            <SCModal
                open={!!showImage}
                showClose={true}
                withCloseButton={true}
                onClose={() => setShowImage(undefined)}
                size={"auto"}>
                <ImageWithZoom
                    attachment={{ ContentType: "image/jpeg", Url: showImage.Inventory?.ImageUrl ?? "", UrlThumb: showImage.Inventory?.ThumbnailUrl ?? "" }}
                />
            </SCModal>
        }
    </>

}

export default PurchasesSectionTable
